import { toast } from 'sonner'
import { create } from 'zustand'

import { useDirectorBridgeStore } from '../../store'

import {
  DEFAULT_ENGINE,
  DEFAULT_FORMAT,
  DEFAULT_RATIO,
  DEFAULT_RES,
  getMaxGuideRefs,
  GUIDE_REFS_CAPACITY
} from './constants'
import { buildReferenceImagesFromGuideRefs } from './lib/build-reference-images'
import { enhancePromptApi, DIRECTOR_ENHANCE_SLUG, parseEnhancedFrames } from './lib/real-enhance'
import { generateStoryboard } from './lib/real-generate'
import { probeImageSize, splitGrid } from './lib/split-grid'
import {
  formatToStoryboardFrameCount,
  formatToStoryboardGridId,
  storyboardGridIdToFormat
} from './lib/storyboard-grid-id'
import { uploadGuideRefFile } from './lib/upload-guide-ref'
import type {
  FlowStep,
  FrameCell,
  GuideRef,
  GuideRefMeta,
  HistoryItem,
  LightboxState
} from './types'

function isBlob(url?: string | null): url is string {
  return Boolean(url && url.startsWith('blob:'))
}

function revoke(url?: string | null) {
  if (isBlob(url)) {
    try {
      URL.revokeObjectURL(url)
    } catch {
      /* ignore */
    }
  }
}

/**
 * Grace window (epoch ms) during which the Director Console must ignore
 * outside interactions. Closing the storyboard fires a focus/blur event that
 * Radix would otherwise treat as an "interact outside" on the console and
 * dismiss it. The console's dismiss guards call `shouldBlockConsoleDismiss`.
 */
let consoleDismissBlockedUntil = 0

/** Prevent the console from being dismissed for `ms` from now. */
export function blockConsoleDismiss(ms = 500): void {
  consoleDismissBlockedUntil = Date.now() + ms
}

/** True while the storyboard is open or just closed (grace window). */
export function shouldBlockConsoleDismiss(): boolean {
  if (useStoryBoardStore.getState().open) return true
  return Date.now() < consoleDismissBlockedUntil
}

const emptyGuideRefs = (): (GuideRef | null)[] =>
  new Array(GUIDE_REFS_CAPACITY).fill(null)

const guideRefUploadTasks = new Map<number, Promise<void>>()

/**
 * Background upload of the user-picked source image (the "setDisplayFromFile"
 * flow). Resolves with the uploaded S3 URL (or null on failure). Awaited by
 * `addToTimeline` so the enhance API always receives an S3 URL for
 * `first_frame`. Survives `close()` because it lives at module scope.
 */
let sourceUploadTask: Promise<string | null> | null = null

async function awaitSourceUpload(): Promise<string | null> {
  if (!sourceUploadTask) return null
  try {
    return await sourceUploadTask
  } catch {
    return null
  }
}

function guideRefFromRemoteUrl(url: string, meta?: GuideRefMeta): GuideRef {
  return {
    url,
    name: meta?.name,
    status: 'success',
    imageUrl: meta?.imageUrl,
    imageUrlV1: meta?.imageUrlV1,
    thumbnailUrl: meta?.thumbnailUrl,
    thumbnailUrlV1: meta?.thumbnailUrlV1,
    showImageUrl: meta?.showImageUrl
  }
}

function beginGuideRefFileUpload(
  get: () => StoryBoardState,
  set: (partial: Partial<StoryBoardState>) => void,
  index: number,
  file: File
): void {
  const previewUrl = URL.createObjectURL(file)
  const refs = get().guideRefs.slice()
  revoke(refs[index]?.url)
  refs[index] = {
    url: previewUrl,
    name: file.name,
    status: 'uploading'
  }
  set({ guideRefs: refs })

  const task = uploadGuideRefFile(file)
    .then(uploaded => {
      const next = get().guideRefs.slice()
      const current = next[index]
      if (!current || current.status !== 'uploading') return
      revoke(current.url)
      next[index] = uploaded
      set({ guideRefs: next })
    })
    .catch(error => {
      console.error('[StoryBoard] guide ref upload failed:', error)
      const next = get().guideRefs.slice()
      const current = next[index]
      if (!current) return
      next[index] = { ...current, status: 'error' }
      set({ guideRefs: next })
    })
    .finally(() => {
      guideRefUploadTasks.delete(index)
    })

  guideRefUploadTasks.set(index, task)
}

async function awaitGuideRefUploads(): Promise<void> {
  await Promise.all([...guideRefUploadTasks.values()])
}

function trimGuideRefsToEngineLimit(
  refs: (GuideRef | null)[],
  engine: string
): (GuideRef | null)[] {
  const max = getMaxGuideRefs(engine)
  const next = refs.slice()
  for (let i = max; i < next.length; i += 1) {
    if (next[i]) {
      revoke(next[i]?.url)
      next[i] = null
    }
  }
  return next
}

interface StoryBoardState {
  open: boolean
  step: FlowStep
  cropDirectEntry: boolean

  format: string
  ratio: string
  res: string
  engine: string
  prompt: string

  guideRefs: (GuideRef | null)[]

  history: HistoryItem[]
  historyDockCollapsed: boolean
  historyPanelOpen: boolean

  /** Currently displayed image (generated composite or uploaded source). */
  display: string | null
  /** Low-res thumbnail for the current display, used as a blur-up placeholder. */
  displayThumb: string | null
  /**
   * Original generation params of the current display when it comes from a
   * history item. Enables the preview "Adjust" action; null for freshly
   * generated / uploaded images.
   */
  displayMeta: {
    prompt: string
    model?: string
    ratio?: string
    format?: string
    gridId?: string | number
  } | null
  /** Source image used for splitting. */
  source: string | null
  /**
   * S3 URL of the source image, populated when the source was uploaded by the
   * user (blob: preview). For generated / history-selected sources, `source`
   * is already an S3 URL and this stays null. Sent as `first_frame` to the
   * enhance API in `addToTimeline`.
   */
  sourceS3Url: string | null
  imgW: number
  imgH: number

  cells: FrameCell[]
  cellEdit: number
  dealAnim: boolean

  generating: boolean
  enhancing: boolean
  splitting: boolean
  /** True while "Add to timeline" is calling the language_model enhance API. */
  addingToTimeline: boolean
  lightbox: LightboxState | null

  // actions
  openStoryBoard: () => void
  openCrop: () => void
  close: () => void
  goStep: (n: FlowStep) => void
  back: () => void

  setFormat: (v: string) => void
  setRatio: (v: string) => void
  setRes: (v: string) => void
  setEngine: (v: string) => void
  setPrompt: (v: string) => void
  enhancePrompt: () => Promise<boolean>

  setGuideRef: (index: number, file: File) => void
  /** Append an uploaded file to the next empty guide slot (respects engine limit). */
  addGuideRefFile: (file: File) => void
  /** Append a remote image URL (gallery / asset library) to the next empty slot. */
  addGuideRefUrl: (url: string, meta?: GuideRefMeta) => void
  removeGuideRef: (index: number) => void

  generate: () => Promise<boolean>

  selectHistory: (item: {
    url: string
    prompt?: string
    thumbnail?: string
    model?: string
    ratio?: string
    format?: string
    gridId?: string | number
  }) => void
  /** Restore the current display's generation params into the composer. */
  adjustFromHistory: () => void
  toggleHistoryDock: () => void
  openHistoryPanel: () => void
  closeHistoryPanel: () => void

  setDisplayFromFile: (file: File) => Promise<void>
  splitFrames: () => Promise<void>
  replaceCell: (index: number, file: File) => void
  setCellEdit: (index: number) => void
  clearDealAnim: () => void

  openLightbox: (url: string, caption: string) => void
  closeLightbox: () => void

  addToTimeline: () => Promise<void>
}

function clearMedia(state: StoryBoardState) {
  // NB: cell URLs are intentionally NOT revoked here — when the flow closes via
  // "Add to timeline" the cells are handed off to the legacy timeline, which
  // keeps referencing those URLs. Split cells are data URLs anyway; only
  // explicitly discarded cells (see `back`) revoke their blobs.
  revoke(state.display)
  if (state.source !== state.display) revoke(state.source)
  state.guideRefs.forEach(r => revoke(r?.url))
  state.history.forEach(h => revoke(h.url))
}

const initialFlow = {
  step: 1 as FlowStep,
  cropDirectEntry: false,
  prompt: '',
  guideRefs: emptyGuideRefs(),
  history: [] as HistoryItem[],
  historyDockCollapsed: false,
  historyPanelOpen: false,
  display: null as string | null,
  displayThumb: null as string | null,
  displayMeta: null as StoryBoardState['displayMeta'],
  source: null as string | null,
  sourceS3Url: null as string | null,
  imgW: 0,
  imgH: 0,
  cells: [] as FrameCell[],
  cellEdit: -1,
  dealAnim: false,
  generating: false,
  enhancing: false,
  splitting: false,
  addingToTimeline: false,
  lightbox: null as LightboxState | null
}

export const useStoryBoardStore = create<StoryBoardState>((set, get) => ({
  open: false,
  format: DEFAULT_FORMAT,
  ratio: DEFAULT_RATIO,
  res: DEFAULT_RES,
  engine: DEFAULT_ENGINE,
  ...initialFlow,

  openStoryBoard: () => {
    clearMedia(get())
    set({
      open: true,
      ...initialFlow,
      guideRefs: emptyGuideRefs(),
      history: [],
      cells: [],
      step: 1,
      cropDirectEntry: false
    })
  },

  openCrop: () => {
    clearMedia(get())
    set({
      open: true,
      ...initialFlow,
      guideRefs: emptyGuideRefs(),
      history: [],
      cells: [],
      step: 2,
      cropDirectEntry: true
    })
  },

  close: () => {
    // Keep the console alive through the focus/blur churn of unmounting.
    blockConsoleDismiss()
    clearMedia(get())
    set({
      open: false,
      ...initialFlow,
      guideRefs: emptyGuideRefs(),
      history: [],
      cells: []
    })
  },

  goStep: n => set({ step: n }),

  back: () => {
    const { step, cropDirectEntry, close, goStep } = get()
    if (step === 3) {
      get().cells.forEach(c => revoke(c.imageUrl))
      set({ cells: [], dealAnim: false })
      if (cropDirectEntry) goStep(2)
      else goStep(1)
    } else if (step === 2) {
      if (cropDirectEntry) close()
      else goStep(1)
    }
  },

  setFormat: v => set({ format: v }),
  setRatio: v => set({ ratio: v }),
  setRes: v => set({ res: v }),
  setEngine: v => {
    const refs = trimGuideRefsToEngineLimit(get().guideRefs, v)
    set({ engine: v, guideRefs: refs })
  },
  setPrompt: v => set({ prompt: v }),

  enhancePrompt: async () => {
    const { prompt, enhancing, format } = get()
    if (enhancing) return false
    const raw = prompt.trim()
    if (!raw) return false
    const grid = formatToStoryboardGridId(format)
    const frame = formatToStoryboardFrameCount(format)
    if (!grid || frame == null) return false
    set({ enhancing: true })
    try {
      const enhanced = await enhancePromptApi({ prompt: raw, frame: String(frame), grid })
      set({ prompt: enhanced, enhancing: false })
      return true
    } catch (error) {
      console.error('[StoryBoard] prompt enhance failed:', error)
      set({ enhancing: false })
      return false
    }
  },

  setGuideRef: (index, file) => {
    const { engine } = get()
    const max = getMaxGuideRefs(engine)
    if (!file || index < 0 || index >= max) return
    beginGuideRefFileUpload(get, set, index, file)
  },

  addGuideRefFile: file => {
    if (!file) return
    const { engine, guideRefs } = get()
    const max = getMaxGuideRefs(engine)
    const index = guideRefs.findIndex((ref, i) => i < max && !ref)
    if (index < 0) return
    beginGuideRefFileUpload(get, set, index, file)
  },

  addGuideRefUrl: (url, meta) => {
    if (!url) return
    const { engine, guideRefs } = get()
    const max = getMaxGuideRefs(engine)
    const index = guideRefs.findIndex((ref, i) => i < max && !ref)
    if (index < 0) return
    const refs = guideRefs.slice()
    revoke(refs[index]?.url)
    refs[index] = guideRefFromRemoteUrl(url, meta)
    set({ guideRefs: refs })
  },

  removeGuideRef: index => {
    const refs = get().guideRefs.slice()
    revoke(refs[index]?.url)
    refs[index] = null
    set({ guideRefs: refs })
  },

  generate: async () => {
    const { format, ratio, engine, prompt, generating, guideRefs } = get()
    if (generating) return false
    const trimmed = prompt.trim()
    if (!trimmed) return false

    await awaitGuideRefUploads()

    const max = getMaxGuideRefs(engine)
    const activeRefs = guideRefs.slice(0, max).filter(Boolean) as GuideRef[]
    if (activeRefs.some(ref => ref.status === 'error')) return false

    const referenceImages = buildReferenceImagesFromGuideRefs(activeRefs)

    set({ generating: true })
    try {
      const url = await generateStoryboard({
        model: engine,
        format,
        ratio,
        prompt: trimmed,
        referenceImages
      })
      const { width, height } = await probeImageSize(url)
      const state = get()
      revoke(state.display)
      if (state.source !== state.display) revoke(state.source)
      state.cells.forEach(c => revoke(c.imageUrl))

      set({
        display: url,
        displayThumb: null,
        displayMeta: null,
        source: url,
        sourceS3Url: null,
        imgW: width,
        imgH: height,
        cells: [],
        cellEdit: -1,
        history: [{ url, prompt: trimmed }, ...state.history],
        generating: false,
        step: 1
      })
      return true
    } catch (error) {
      console.error('[StoryBoard] generation failed:', error)
      set({ generating: false, splitting: false })
      return false
    }
  },

  selectHistory: item => {
    const resolvedFormat =
      item.format ?? storyboardGridIdToFormat(item.gridId)
    set({
      display: item.url,
      displayThumb: item.thumbnail ?? null,
      displayMeta: {
        prompt: item.prompt ?? '',
        model: item.model,
        ratio: item.ratio,
        format: resolvedFormat,
        gridId: item.gridId
      },
      source: item.url,
      sourceS3Url: null,
      cells: [],
      cellEdit: -1,
      step: 1,
      ...(resolvedFormat ? { format: resolvedFormat } : {}),
      ...(item.ratio ? { ratio: item.ratio } : {}),
      ...(item.model ? { engine: item.model } : {})
    })
    void probeImageSize(item.url).then(({ width, height }) => {
      if (width && height) set({ imgW: width, imgH: height })
    })
  },

  adjustFromHistory: () => {
    const meta = get().displayMeta
    if (!meta) return
    const patch: Partial<StoryBoardState> = { prompt: meta.prompt }
    if (meta.format) patch.format = meta.format
    if (meta.ratio) patch.ratio = meta.ratio
    if (meta.model) {
      patch.engine = meta.model
      patch.guideRefs = trimGuideRefsToEngineLimit(get().guideRefs, meta.model)
    }
    set(patch)
  },

  toggleHistoryDock: () =>
    set(s => ({ historyDockCollapsed: !s.historyDockCollapsed })),
  openHistoryPanel: () => set({ historyPanelOpen: true }),
  closeHistoryPanel: () => set({ historyPanelOpen: false }),

  setDisplayFromFile: async file => {
    if (!file || !file.type.startsWith('image/')) return
    const state = get()
    revoke(state.display)
    if (state.source !== state.display) revoke(state.source)
    state.cells.forEach(c => revoke(c.imageUrl))
    const url = URL.createObjectURL(file)
    const { width, height } = await probeImageSize(url)
    set({
      display: url,
      displayThumb: null,
      displayMeta: null,
      source: url,
      sourceS3Url: null,
      imgW: width,
      imgH: height,
      cells: [],
      cellEdit: -1,
      step: get().step === 1 ? 2 : get().step
    })

    // Kick off a background upload so `first_frame` can be an S3 URL when the
    // user later clicks "Add to timeline". The promise resolves with the S3
    // URL (or null on failure) and is awaited by `addToTimeline`. Also writes
    // `sourceS3Url` into the store so other readers can observe progress.
    let currentTask: Promise<string | null> | null = null
    currentTask = (async () => {
      try {
        const uploaded = await uploadGuideRefFile(file)
        if (get().source === url) {
          set({ sourceS3Url: uploaded.imageUrl })
        }
        return uploaded.imageUrl || null
      } catch (error) {
        console.error('[StoryBoard] source image upload failed:', error)
        return null
      } finally {
        if (sourceUploadTask === currentTask) sourceUploadTask = null
      }
    })()
    sourceUploadTask = currentTask
  },

  splitFrames: async () => {
    const { source, display, format, displayMeta } = get()
    const url = source || display
    if (!url) return
    // History items carry their original grid layout (from `grid_id`); prefer it
    // over the composer's current format so re-cropping matches how it was made.
    const splitFormat =
      displayMeta?.format ??
      storyboardGridIdToFormat(displayMeta?.gridId) ??
      format
    set({ splitting: true })
    try {
      const urls = await splitGrid(url, splitFormat)
      if (!urls.length) {
        set({ splitting: false })
        toast.error('Failed to split frames')
        return
      }
      get().cells.forEach(c => revoke(c.imageUrl))
      set({
        cells: urls.map(imageUrl => ({ imageUrl })),
        format: splitFormat,
        cellEdit: -1,
        dealAnim: true,
        step: 3,
        splitting: false
      })
    } catch (error) {
      console.error('[StoryBoard] split frames failed:', error)
      set({ splitting: false })
      toast.error('Failed to split frames')
    }
  },

  replaceCell: (index, file) => {
    if (!file || !file.type.startsWith('image/')) return
    const cells = get().cells.slice()
    if (!cells[index]) return
    revoke(cells[index].imageUrl)
    cells[index] = { imageUrl: URL.createObjectURL(file) }
    set({ cells })
  },

  setCellEdit: index => set({ cellEdit: index }),
  clearDealAnim: () => set({ dealAnim: false }),

  openLightbox: (url, caption) => set({ lightbox: { url, caption } }),
  closeLightbox: () => set({ lightbox: null }),

  addToTimeline: async () => {
    const { cells, format, source, sourceS3Url, addingToTimeline } = get()
    if (addingToTimeline) return
    const urls = cells.map(c => c.imageUrl).filter(Boolean)
    if (!urls.length) {
      get().close()
      return
    }
    const actions = useDirectorBridgeStore.getState().actions
    if (!actions?.addStoryUnitsPending || !actions?.setStoryUnitPrompts) {
      // 导演台未挂载或为旧版本——保持弹窗打开并提示，避免"静默关闭"。
      console.error('[StoryBoard] director console actions not available')
      toast.error('Open the Director Console first')
      return
    }

    const grid = formatToStoryboardGridId(format)
    const frame = formatToStoryboardFrameCount(format)

    // 1) Add images to the timeline immediately and close the storyboard so
    //    the user sees their frames right away. Each unit is flagged
    //    `promptPending: true`, which renders a breathing light on the prompt
    //    area until the enhance API resolves.
    set({ addingToTimeline: true })
    let ids: string[] = []
    try {
      ids = actions.addStoryUnitsPending(urls)
    } catch (error) {
      console.error('[StoryBoard] addStoryUnitsPending failed:', error)
      toast.error('Failed to add to timeline')
      set({ addingToTimeline: false })
      return
    }
    get().close()

    // 2) Kick off the language-model enhance API in the background. Capture
    //    everything we need before close since the store state is reset.
    const capturedSourceS3Url = sourceS3Url
    const capturedSource = source
    void (async () => {
      try {
        // Wait for the source image's background S3 upload (if any) so
        // first_frame is always an S3 URL rather than a local blob: URL.
        const uploadedS3 = await awaitSourceUpload()
        const firstFrame = capturedSourceS3Url ?? uploadedS3 ?? capturedSource ?? undefined

        let promptArr: string[]
        if (grid && frame != null) {
          console.log('[StoryBoard] calling language_model', {
            slug: DIRECTOR_ENHANCE_SLUG,
            first_frame: firstFrame,
            frame: String(frame),
            grid
          })
          const raw = await enhancePromptApi({
            slug: DIRECTOR_ENHANCE_SLUG,
            firstFrame,
            frame: String(frame),
            grid
          })
          const frames = parseEnhancedFrames(raw)
          if (frames && frames.length) {
            promptArr = ids.map((_, i) => frames[i]?.prompt ?? '')
            console.log('[StoryBoard] enhanced frames:', frames.length)
          } else {
            promptArr = ids.map(() => raw)
            console.log('[StoryBoard] enhanced prompt (fallback):', raw)
          }
        } else {
          promptArr = ids.map(() => '')
        }
        actions.setStoryUnitPrompts(ids, promptArr)
      } catch (error) {
        console.error('[StoryBoard] add-to-timeline enhance failed:', error)
        toast.error('Failed to enhance prompt')
        // Clear the pending state so the breathing light stops, leaving the
        // units editable with empty prompts.
        try {
          actions.setStoryUnitPrompts(ids, ids.map(() => ''))
        } catch {
          /* ignore */
        }
      }
    })()
  }
}))